import { useRef, useState } from "react";
import queryString from "query-string";
import { getDateStringWithGeAndLt } from "../../../../components/Reports/ReportHeader";
import {
  getYearAndWeekNumber,
  generateNewYearArray,
  DaysOfTheWeekEnum,
  weeksInYear,
  isSameDate,
} from "reports/pages/utils";
import LoadingCircle from "reports/components/simple/LoadingCircle";
import {
  DataDumpAggregateReportsType,
  PciReportProps,
  ReportParams,
} from "../../../../types";
import { ReportStyles } from "../Styles/PciReportStyles";
import { useParams, useLocation } from "react-router";
import {
  processYearlyAndShiftDataForCharts,
  generateUsablePciDataFromDateRanges,
  pciMachines,
} from "../Utils/pciUtils";
import {
  FORMAT_READABLE_DATE,
  EASTERN_STANDARD_TIMEZONE,
  ONE_HOUR_OF_MS,
} from "reports/utils/timeUtils";
import PciMachineReport from "../ReportContent/Reports/PciMachineReport";
import DataDumpGetter from "../../../../getters/DataDumpGetter";
import { useGetShifts } from "reports/utils/shiftsHook";
import { PCI_PIPELINE_NUM_WEEKS } from "./pciPipelineConstants";
import { useMidgardProcesses } from "reports/components/Dashboard/useFetchProcesses";

export default function PciMachinePipeline() {
  const location = useLocation();
  const { machineName } = useParams() as ReportParams;
  const tenant = "pci";
  const values = queryString.parse(location.search);
  const today = useRef(new Date());

  const processes = useMidgardProcesses();

  const process = processes?.find(process => process.name === machineName);
  const machemetryId = process?.taps?.[0]?.machemetryId || "";
  const { machineDetails, reportType } = machineName
    ? pciMachines[machineName.toUpperCase()]
    : {
        machineDetails: undefined,
        reportType: undefined,
      };

  // we will use the extra details, oee, targets, as required in the future but not all at once
  const { oeeTarget } = machineDetails || {};

  /* GraphQL Query to get Shift Segments */
  const shouldSkipQuery = false;

  /**
   * This section is used to determine the year based data
   */
  today.current.setHours(2); // added two hours for daylight savings
  today.current.setMilliseconds(1); // increment by one so it shows were IN the day for calculations
  today.current.setSeconds(0);
  today.current.setMinutes(0);

  const timeZone = EASTERN_STANDARD_TIMEZONE;
  const dayOfWeek = 1;

  const [, currentWeekNumber] = getYearAndWeekNumber({
    d: today.current,
    timeZone,
    dayOfWeek,
  });

  const isPrevWeekFromLastYear = currentWeekNumber === 1;
  const arePrevWeeksFromLastYear = currentWeekNumber <= 10;

  const reportYear = isPrevWeekFromLastYear
    ? today.current.getFullYear() - 1
    : today.current.getFullYear();

  const numWeeksInReportYear = weeksInYear({
    year: reportYear,
    timeZone,
  });

  const previousWeekNumber = isPrevWeekFromLastYear
    ? numWeeksInReportYear
    : currentWeekNumber - 1; // we get data for the last completed week

  const yearDateArray = generateNewYearArray(
    DaysOfTheWeekEnum.Monday,
    arePrevWeeksFromLastYear
      ? today.current.getFullYear() - 1
      : today.current.getFullYear(),
    timeZone
  ); // generate time ranges required for yearly date

  const yearDateArray2 = generateNewYearArray(
    DaysOfTheWeekEnum.Monday,
    arePrevWeeksFromLastYear
      ? today.current.getFullYear()
      : today.current.getFullYear() + 1,
    timeZone
  );

  const twoYearDateArray = yearDateArray.concat(
    yearDateArray2.slice(1, yearDateArray.length)
  );

  const yearDataDumpSetArray = generateUsablePciDataFromDateRanges(
    twoYearDateArray,
    currentWeekNumber,
    tenant,
    machemetryId,
    shouldSkipQuery,
    weeksInYear({
      year: today.current.getFullYear() - 1,
      timeZone,
    })
  );

  const previousWeekNumberIndex =
    new Date(twoYearDateArray[0].ge).getFullYear() === reportYear
      ? previousWeekNumber
      : previousWeekNumber + numWeeksInReportYear;

  // these times are used for the shift data portion and not the yearly
  // if our ge and lt parameters are not populated we need to pick the most recent week instead
  const areParametersPopulated =
    location.search !== "" && values.ge && values.lt;
  const ge = areParametersPopulated
    ? Number(values.ge)
    : twoYearDateArray[previousWeekNumberIndex].ge.getTime(); // getTime returns the Date object in milliseconds epoch time
  const lt = areParametersPopulated
    ? Number(values.lt)
    : twoYearDateArray[previousWeekNumberIndex].lt.getTime();

  // TODO: find a way to deal with dalight savings time here (for now we push the time forward as a hack)
  const targetWeekGeStart = ge + ONE_HOUR_OF_MS * 2;
  const [, targetWeekNumber] = getYearAndWeekNumber({
    d: new Date(targetWeekGeStart),
    timeZone,
    dayOfWeek,
  }); // week number referenced by given GE/LT parameters in URL, used for downtime Pareto chart

  /**
   * This section is used to determine the shift based data
   */
  const shiftSegments = useGetShifts(
    {
      ge: ge.toString(),
      lt: lt.toString(),
    },
    machemetryId
  );

  const [dataDumpsOfShifts, setDataDumpsOfShifts] = useState([] as any[]);
  const [targetWeekAggregateData, setTargetWeekAggregateData] =
    useState<DataDumpAggregateReportsType>();

  // we need to get the offset of our maximum week (current true), and our target week
  // this is so we can target which week of data we grab for determining tag information
  const currentWeekInData = arePrevWeeksFromLastYear
    ? currentWeekNumber
    : currentWeekNumber - targetWeekNumber;

  const targetWeekDateRange = twoYearDateArray.find(({ ge }) =>
    isSameDate({ date1: ge, date2: new Date(targetWeekGeStart) })
  );

  const {
    completedYearlyData,
    shiftsDataArray,
    filteredtotalTimePerTagLastWeekData,
    lastWeeksWeeklyResults,
    totalTimePerTagTargetWeekAggregateData,
  } = processYearlyAndShiftDataForCharts(
    yearDataDumpSetArray,
    currentWeekInData,
    dataDumpsOfShifts,
    machineDetails,
    shiftSegments,
    targetWeekAggregateData,
    targetWeekGeStart
  );

  const subHeader = `${tenant.toUpperCase()} - ${machineName} - Philadelphia, PA`;
  const dateString = getDateStringWithGeAndLt(ge, lt, FORMAT_READABLE_DATE);

  const reportProps = {
    dateString,
    subHeader,
    currentWeekNumber,
    completedYearlyData,
    shiftsDataArray,
    filteredtotalTimePerTagLastWeekData,
    lastWeeksWeeklyResults,
    machineName,
    totalTimePerTagTargetWeekAggregateData,
    oeeTarget,
  } as PciReportProps;

  // used to check if all data is loaded, otherwise we have flicker
  const completedLoading =
    dataDumpsOfShifts.length === // ensure we have all our shifts
      shiftSegments.filter(
        ({ shift }) =>
          !["Not Scheduled", "Shift Change"].includes(shift.name as string)
      ).length &&
    targetWeekAggregateData && // this data needs to be there for downtime pareto
    yearDataDumpSetArray.length === PCI_PIPELINE_NUM_WEEKS; // we need PCI_PIPELINE_NUM_WEEKS data points

  document.title = `WEEKLY ${machineName} PERFORMANCE REPORT OVERVIEW - ${dateString}`;
  return shiftSegments.length <= 0 ? (
    <LoadingCircle />
  ) : (
    <ReportStyles
      key={`WEEKLY ${machineName} PERFORMANCE REPORT OVERVIEW - ${dateString}`}
    >
      {shiftSegments.map(({ shift, range }) => {
        // get data for each shift
        if (!["Not Scheduled", "Shift Change"].includes(shift.name as string)) {
          return (
            <DataDumpGetter
              setData={setDataDumpsOfShifts}
              key={range.ge}
              range={range}
              machemetryIds={[machemetryId]}
              shouldSkipQuery={shouldSkipQuery}
              multiData={true}
            />
          );
        } else {
          return null;
        }
      })}
      <DataDumpGetter
        setData={setTargetWeekAggregateData}
        isAggregate={true}
        key={targetWeekDateRange?.ge?.getTime()}
        range={{
          ge: targetWeekDateRange?.ge?.getTime() || 0,
          lt: targetWeekDateRange?.lt?.getTime() || 0,
        }}
        tenant={tenant}
        machemetryIds={[machemetryId]}
        shouldSkipQuery={shouldSkipQuery}
        multiData={false}
      />
      {completedLoading ? (
        <PciReport {...{ reportProps, reportType }} />
      ) : (
        <LoadingCircle />
      )}
    </ReportStyles>
  );
}

function PciReport({
  reportProps,
  reportType,
}: {
  reportProps: PciReportProps;
  reportType?: number;
}) {
  if (reportType === 1) {
    return <PciMachineReport {...reportProps} />;
  } else {
    return null;
  }
}
